import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { resetSession } from '../utils/streamApi';
import { unregisterPushOnLogout } from '../utils/pushNotifications';
import { useSidebar } from '../contexts/SidebarContext';
import { useEntitlements, canAccessMenu } from '../utils/entitlements';
import useIsMobile from '../utils/useIsMobile';
import { useServerUnreachable } from '../utils/serverReachability';
import '../css/sidebar.css';

// Visibility is driven by per-role menu permissions from the server (see
// entitlements.js / MENU_REGISTRY). Each link carries a `menu` key; industrial
// links also need clientType 'industrial'. Admins configure who sees what in the
// User & Roles screen.
export const NAV_LINKS = [
  // ── Operational ──
  // Guided Setup leads: on a home box it is often the only config a customer
  // ever opens, and they come back to it to fix false alerts.
  { path: '/guided-setup', label: 'Guided Setup',   icon: '🧭', menu: 'guided-setup' },
  { path: '/',             label: 'Cameras',        icon: '📹', menu: 'cameras' },
  // The mobile Dashboard's Play Devices + Pause Devices tiles, on one page.
  // desktopOnly: on a phone the Dashboard tiles already do this.
  { path: '/devices',      label: 'Play & Pause Devices', icon: '⏯', menu: 'cameras', desktopOnly: true },
  { path: '/alerts',       label: 'Alerts',         icon: '🚨', menu: 'alerts' },
  { path: '/recordings',   label: 'Recordings',     icon: '📼', menu: 'recordings' },
  { path: '/known-seen',   label: 'Known Seen',     icon: '✅', menu: 'known-seen' },
  { path: '/guard-checkin',       label: 'My Patrol',      icon: '🛡️', menu: 'guard-checkin' },
  { path: '/guard-patrol/report', label: 'Patrol Report', icon: '🗒️', menu: 'guard-patrol-report' },
  { path: '/my-attendance',       label: 'My Attendance', icon: '🕘', menu: 'attendance-my' },
  { path: '/attendance-review',   label: 'Attendance Review', icon: '📋', menu: 'attendance-review' },

  // ── Industrial console (clientType 'industrial') — placed above Admin so the
  //    industrial group leads on industrial sites; hidden entirely on home sites. ──
  { path: '/industrial',            label: 'Ops Console',    icon: '🏭', section: 'Industrial', menu: 'industrial-ops', industrial: true },
  { path: '/industrial/attendance', label: 'Attendance',     icon: '🕘', menu: 'industrial-attendance', industrial: true },
  { path: '/industrial/wall',       label: 'Camera Wall',    icon: '🧱', menu: 'industrial-wall', industrial: true },
  { path: '/industrial/ppe',        label: 'PPE Live View',  icon: '⛑️', menu: 'industrial-ppe', industrial: true },
  { path: '/industrial/reports',    label: 'Reports',        icon: '📈', menu: 'industrial-reports', industrial: true },
  { path: '/industrial/access',     label: 'Access Control', icon: '🔑', menu: 'industrial-access', industrial: true },
  { path: '/industrial/modules',    label: 'AI Modules',     icon: '🧩', menu: 'industrial-modules', industrial: true },
  { path: '/industrial/counting',   label: 'Object Intelligence Config', icon: '🔢', menu: 'industrial-counting', industrial: true },
  { path: '/industrial/counting/report', label: 'Object Intelligence Report', icon: '📋', menu: 'industrial-count-report', industrial: true },
  { path: '/industrial/footfall',   label: 'Footfall Report', icon: '🚶', menu: 'industrial-footfall', industrial: true },
  { path: '/industrial/vehicles',   label: 'Vehicle Report', icon: '🚗', menu: 'industrial-vehicle-report', industrial: true },
  { path: '/industrial/seats',      label: 'Seat Zones',      icon: '💺', menu: 'industrial-seats', industrial: true },
  { path: '/industrial/seat-report', label: 'Seat Occupancy', icon: '🪑', menu: 'industrial-seat-report', industrial: true },
  { path: '/industrial/cleaning',   label: 'Cleaning Zones',  icon: '🧹', menu: 'industrial-cleaning', industrial: true },
  { path: '/industrial/cleaning/report', label: 'Cleaning Report', icon: '🧽', menu: 'industrial-cleaning-report', industrial: true },
  { path: '/industrial/analyzer',   label: 'Video Analyzer',  icon: '🎬', menu: 'industrial-analyzer', industrial: true },
  // Scheduled analytics: the REPORT belongs with the daily reports above; its
  // config sits under Admin with the other pages that change what the box does.
  { path: '/industrial/analytics/report', label: 'Shop Analytics Report', icon: '🛒', menu: 'industrial-analytics-report', industrial: true },
  { path: '/industrial/analytics', label: 'Shop Analytics Config', icon: '🛠️', menu: 'industrial-analytics', industrial: true },

  // ── Config / master data + analytics ──
  { path: '/faces/known',  label: 'Known Faces',    icon: '👤', section: 'Admin', menu: 'known-faces' },
  { path: '/faces/add',    label: 'Add to Known',   icon: '➕', menu: 'known-faces-add' },
  { path: '/faces/banned', label: 'Banned',         icon: '🚫', menu: 'banned' },
  { path: '/detections',   label: 'Detection Logs', icon: '🔎', menu: 'detections' },
  { path: '/forensic',     label: 'Analysis',       icon: '📊', menu: 'analysis' },
  // Industrial only: it correlates badge readers against cameras, and a home has
  // no access-control system to correlate against. Listed here rather than in the
  // industrial block because buildIndustrialMenu pulls it in by key.
  { path: '/access-events', label: 'Access Events', icon: '🪪', menu: 'pacs-access', industrial: true },
  { path: '/device-alerts', label: 'Device Alerts', icon: '🚨', menu: 'device-alerts' },
  { path: '/config-changelog', label: 'Config Changelog', icon: '🧾', menu: 'config-changelog' },
  { path: '/learning-report', label: 'Learning Report', icon: '🧠', menu: 'learning-report' },
  { path: '/diagnostics',  label: 'Diagnostics',    icon: '🩺', menu: 'diagnostics' },
  { path: '/guard-patrol', label: 'Guard Patrol',   icon: '🛡️', menu: 'guard-patrol' },
  { path: '/attendance-setup', label: 'Attendance Setup', icon: '🕘', menu: 'attendance-config' },
  { path: '/admin-config', label: 'Admin Config',   icon: '⚙️', menu: 'admin-config' },
  { path: '/user-roles',   label: 'Users & Roles',  icon: '🔐', menu: 'user-roles' },
  // App Support (PRVSK-109): support access, share logs, mobile app logs, server logs.
  { path: '/support',      label: 'App Support',    icon: '🛟', menu: 'user-roles', appSupport: true },
];

// Shown at the top of the menu only while the server is unreachable: every
// other page needs the server, but Local Mode plays cameras straight from the
// LAN and reads alerts saved on this device. No `menu` key — it's not a
// server-granted page, and with the server down there's no one to ask.
export const LOCAL_MODE_LINK = { path: '/local', label: 'Local Mode', icon: '📡' };

// Industrial sites get a flattened, reordered menu:
//   • no "Industrial" heading — its items lead the first (unlabelled) group
//   • no standalone Cameras / Alerts — reached from the Camera Wall / Ops Console
//     pages (in-page buttons), so they're dropped from the sidebar here
//   • Known Seen + AI Modules live under Admin
// Home sites are untouched (this only runs when clientType === 'industrial').
function buildIndustrialMenu(links) {
  const by = k => links.find(l => l.menu === k);
  const strip = l => l && { ...l, section: undefined };
  const firstKeys = [
    'industrial-ops', 'industrial-wall', 'industrial-ppe', 'industrial-attendance',
    'guard-checkin', 'guard-patrol-report',
    'attendance-my', 'attendance-review', 'industrial-reports',
    'industrial-access', 'industrial-footfall', 'industrial-vehicle-report', 'industrial-analytics-report', 'industrial-seat-report',
    'industrial-cleaning-report',
    'industrial-counting', 'industrial-seats', 'industrial-cleaning', 'industrial-count-report', 'recordings',
  ];
  const adminKeys = [
    // Guided Setup leads the admin group. It was in NEITHER list, so the flattened
    // industrial menu dropped it entirely: SetupGate still force-opened the wizard
    // on first login, but nobody could get back to it afterwards — and coming back
    // to it is how a site fixes a false alert or switches a module on later.
    'guided-setup',
    // Video Analyzer sits under Admin, not with the daily reports: it is an offline
    // tool that saturates the CPU of whatever box runs it, so it belongs with the
    // config pages an operator does not open during a shift.
    'known-seen', 'industrial-modules', 'industrial-analyzer', 'industrial-analytics',
    'guard-patrol', 'attendance-config', 'known-faces', 'known-faces-add', 'banned',
    // Access Events is listed here too, or the flattened industrial menu would
    // drop it entirely — and a badged site is exactly where it earns its place.
    'pacs-access',
    'detections', 'analysis', 'device-alerts', 'config-changelog',
    // In BOTH this list and LINKS, or the flattened industrial menu drops it —
    // the same way Guided Setup was dropped, noted above.
    'learning-report',
    'diagnostics', 'admin-config', 'user-roles',
  ];
  const first = firstKeys.map(by).filter(Boolean).map(strip);
  // No "Admin" heading — items flow after the main group with a plain divider only.
  // Support shares Users & Roles' menu key, so it's picked up by path, not by `by`.
  const admin = [...adminKeys.map(by), links.find(l => l.path === '/support')].filter(Boolean)
    .map((l, i) => ({ ...l, section: undefined, divider: i === 0 }));
  return [...first, ...admin];
}

export default function Sidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const { collapsed, toggleCollapse, mobileOpen, closeMobile } = useSidebar();
  const isMobile = useIsMobile();
  const entitlements = useEntitlements();
  const unreachable = useServerUnreachable();

  // Hide links the logged-in role's menu permissions don't allow.
  const visibleLinks = NAV_LINKS.filter(l => canAccessMenu(l, entitlements) && !(isMobile && l.desktopOnly));
  // Industrial sites get the flattened/reordered console menu; home sites unchanged.
  const roleLinks = entitlements.clientType === 'industrial'
    ? buildIndustrialMenu(visibleLinks)
    : visibleLinks;
  const displayLinks = unreachable ? [LOCAL_MODE_LINK, ...roleLinks] : roleLinks;

  // Base routes that have deeper siblings must match exactly, else the base
  // link stays highlighted on every sub-route (e.g. /industrial vs /industrial/wall).
  const EXACT_PATHS = new Set(['/', '/industrial', '/industrial/counting']);
  const isActive = path =>
    EXACT_PATHS.has(path) ? location.pathname === path : location.pathname.startsWith(path);

  // Close mobile drawer on any route change (back button, programmatic navigate, etc.)
  useEffect(() => { closeMobile(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // '/' redirects back to /dashboard on mobile (by design) — use the
  // unguarded alias so "Cameras" actually opens MyCamerasPage from here.
  const handleNav = path => { navigate(isMobile && path === '/' ? '/cameras' : path); closeMobile(); };

  // Mobile-only: the drawer's full-screen backdrop sits above the navbar, so the
  // profile-menu Logout (Navbar.jsx) is untappable while this drawer is open —
  // surface a Logout here too so it isn't effectively hidden on mobile.
  const doLogout = () => { unregisterPushOnLogout(); resetSession(); dispatch(logout()); navigate('/login', { replace: true }); };

  // JS-based mobile positioning — guards against CSS media query mismatches
  // (e.g. Chrome transiently reporting wrong viewport width during navigation).
  const mobileStyle = isMobile ? {
    position: 'fixed', top: 0, left: 0, height: '100vh', width: 240, zIndex: 300,
    transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: mobileOpen ? '4px 0 32px rgba(0,0,0,0.45)' : 'none',
  } : undefined;

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={closeMobile} />}

      {/* `collapsed` is a desktop affordance and defaults to true (SidebarContext),
          so on mobile it would open the drawer icon-only on any browser that has
          never expanded it. Drop the class on mobile entirely — same JS source of
          truth as mobileStyle above, so it can't disagree with a media query. */}
      <aside className={`sidebar${collapsed && !isMobile ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`} style={mobileStyle}>

        <div className="sidebar-toggle">
          <button className="sidebar-toggle-btn" onClick={toggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>

        <nav className="sidebar-nav">
          {isMobile && (
            <button
              className="sidebar-link"
              onClick={() => handleNav('/dashboard')}
              data-label="Dashboard"
            >
              <span className="sidebar-icon">🏠</span>
              <span className="sidebar-label">Dashboard</span>
            </button>
          )}
          {displayLinks.map(l => (
            <div key={l.path} style={{ display: 'contents' }}>
              {l.divider && (
                <div className="sidebar-divider" style={{ height: 1, background: 'var(--ind-line, rgba(255,255,255,0.08))', margin: '8px 12px' }} />
              )}
              {l.section && (
                <div className="sidebar-section" data-label={l.section}>{l.section}</div>
              )}
              <button
                className={`sidebar-link${isActive(l.path) ? ' active' : ''}`}
                onClick={() => handleNav(l.path)}
                data-label={l.label}
                title={l.label}
              >
                <span className="sidebar-icon">{l.icon}</span>
                <span className="sidebar-label">
                  {l.label}
                  {l.beta && <span className="sidebar-beta">β</span>}
                </span>
              </button>
            </div>
          ))}
        </nav>

        {isMobile && (
          <div className="sidebar-footer">
            <button className="sidebar-link sidebar-logout" onClick={doLogout} data-label="Logout">
              <span className="sidebar-icon">⏻</span>
              <span className="sidebar-label">Logout</span>
            </button>
          </div>
        )}

      </aside>
    </>
  );
}
